import React, { useState, useEffect, useMemo } from 'react';
import { Grid, Button } from '@material-ui/core';
import { Formik, Form } from 'formik';
import { useSubscription } from '@apollo/client';
import gql from 'graphql-tag';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import TaskOutput from './components/task-output';
import backIcon from 'core/components/screen-header/go-to.svg';
import { Label } from 'core/components/label';
import { Card } from 'core/components/card';
import StateMachineOutput from './components/state-machine-output';
import LiveTaskMessages from './components/live-task-messages';
import TaskHistory from './components/task-history';
import { isJson } from 'tools/helpers';
import { useToolDescription } from 'tools/hooks/use-tool-description';
import GenerateTaskForm from './components/generate-task-form';

import useStyles from './generate-task-ui.styles';

const TASK_LIVE_OUTPUT_SUBSCRIPTION = gql`
  subscription onTaskLiveOutput($taskRunId: String!) {
    onTaskLiveOutput(taskRunId: $taskRunId) {
      taskRunId
      message
      type
    }
  }
`;

const parseOutput = (output) => {
  if (!output) {
    return undefined;
  }
  if (output.type === 'OUTPUT' && output?.message) {
    return { type: 'OUTPUT', ...JSON.parse(output.message) };
  }
  if (output.type === 'ERROR') {
    return output;
  }
  return undefined;
};

/**
 * GenerateTaskUI component renders a formik form for a given task.
 * This component will work if task fires state machine or a single mutation that triggers a lambda.
 *
 * @component
 * @param {string} name - The name of the task.
 * @param {string} description - The description of the task.
 * @param {Object} parameters - The parameters required for the task.
 * @param {Function} onSubmit - The function to be called when the form is submitted.
 * @param {boolean} loading - Indicates whether the task is currently running.
 * @param {Object} error - The error object if there was an error during task execution.
 * @param {Object} response - The response object containing the task output. If task started a state machine, this will have the taskRunId.
 * @param {Object} validationSchema - The Yup validation schema for the form.
 * @param {boolean} disabled - Whether the form should be disabled.
 * @returns {JSX.Element} The rendered GenerateTaskUI component.
 */
const GenerateTaskUI = ({
  name,
  description: descriptionFromProps,
  parameters,
  onSubmit,
  loading,
  error,
  response,
  validationSchema,
  disabled = false
}) => {
  const classes = useStyles();
  const [runStarted, setRunStarted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [output, setOutput] = useState();
  const [dismissedResponse, setDismissedResponse] = useState();
  // Run Again masks the completed run by reference; the next submit returns a
  // new response object from Apollo, so the new run's result displays normally
  const activeResponse = response === dismissedResponse ? undefined : response;

  // hide history and the tools-home back link if we're using this component in a dashboard
  const { pathname } = useLocation();
  const fromDashboard = pathname.includes('dashboard');

  const descriptionInConstants = useToolDescription();
  // the route-derived description belongs to the dashboard tool, not the embedded task card
  const description = descriptionFromProps || (fromDashboard ? undefined : descriptionInConstants);

  const keys = Object.keys(parameters);
  const initialValues = keys.reduce((acc, key) => {
    acc[key] = parameters[key].default;
    return acc;
  }, {});

  const taskRunId = activeResponse?.taskRunId;
  const parsedTaskOutput =
    activeResponse?.details && isJson(activeResponse?.details)
      ? JSON.parse(activeResponse?.details)
      : activeResponse?.details;
  // need to check if parsedTaskOutput is an object and not an array because typeof array is object and we want [{...}] not [[{...}]]
  const taskOutput = useMemo(
    () =>
      parsedTaskOutput && typeof parsedTaskOutput === 'object' && !Array.isArray(parsedTaskOutput)
        ? [parsedTaskOutput]
        : parsedTaskOutput,
    [parsedTaskOutput]
  );
  const success = activeResponse?.__typename === 'ToolSuccess' || parsedTaskOutput?.success;
  const stateMachineOutput = parseOutput(output);

  // bulk (CSV) tasks re-run by uploading a new file, so they don't get Run Again
  const isBulkTask = Object.values(parameters).some((param) => param.type === 'file' || param.type === 'csvFile');

  const handleRunAgain = () => {
    setDismissedResponse(response);
    setOutput(undefined);
    setMessages([]);
    setRunStarted(false);
  };

  const { data: subscriptionData } = useSubscription(TASK_LIVE_OUTPUT_SUBSCRIPTION, {
    variables: { taskRunId },
    skip: !taskRunId
  });

  useEffect(() => {
    // for state machine support
    const type = subscriptionData?.onTaskLiveOutput?.type;
    if (subscriptionData?.onTaskLiveOutput?.message && (!type || type === 'MESSAGE')) {
      setMessages((prevMessages) => [...prevMessages, subscriptionData.onTaskLiveOutput.message]);
    }
    if (subscriptionData?.onTaskLiveOutput?.message && (type === 'OUTPUT' || type === 'ERROR')) {
      setOutput(subscriptionData.onTaskLiveOutput);
      setRunStarted(false);
    }
  }, [subscriptionData]);

  useEffect(() => {
    if (taskRunId && !runStarted) {
      // handle loading state if state machine is started
      setRunStarted(true);
    }
  }, [taskRunId, runStarted]);

  useEffect(() => {
    if (taskOutput || stateMachineOutput) {
      setRunStarted(false);
    }
  }, [taskRunId, stateMachineOutput, taskOutput]);

  return (
    <>
      {!fromDashboard && (
        <Link to="/staff/tools">
          <Grid container alignItems="center">
            <Button variant="text" color="secondary">
              <img src={backIcon} className={classes.icon} alt="back" />
              Back to Tools Home
            </Button>
          </Grid>
        </Link>
      )}
      <Card type="secondary" className={classes.card}>
        <Grid>
          {name && (
            <Label type="titleLight" className={classes.title}>
              {name}
            </Label>
          )}
          {description && <Label type="smallSubtitleLight">{description}</Label>}
          <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
            validateOnChange={false}
            validateOnBlur={false}
          >
            <Form>
              <GenerateTaskForm
                parameters={parameters}
                name={name}
                loading={loading}
                runStarted={runStarted}
                taskOutput={taskOutput}
                stateMachineOutput={stateMachineOutput}
                disabled={disabled}
                onRunAgain={isBulkTask ? undefined : handleRunAgain}
              />
            </Form>
          </Formik>
        </Grid>
      </Card>
      {(loading || (runStarted && !taskOutput && !stateMachineOutput && !messages?.length)) && (
        <Label type="subtitle">Starting task...</Label>
      )}
      {(error || activeResponse?.error) && (
        <Label className={classes.output} type="subtitle">
          There was an error: {error?.message || activeResponse?.error}
        </Label>
      )}
      <LiveTaskMessages messages={messages} taskRunId={taskRunId} doneRunning={taskOutput || stateMachineOutput} />
      {taskOutput && <TaskOutput success={success} taskOutput={taskOutput} />}
      {stateMachineOutput && <StateMachineOutput stateMachineOutput={stateMachineOutput} taskRunId={taskRunId} />}
      {!fromDashboard && <TaskHistory title={name} doneRunning={taskOutput || stateMachineOutput} />}
    </>
  );
};

GenerateTaskUI.propTypes = {
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  parameters: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.object,
  response: PropTypes.object,
  disabled: PropTypes.bool,
  validationSchema: PropTypes.object
};

export default GenerateTaskUI;
